import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthShell from "@/components/auth/AuthShell";

const FIELDS = [
  { key: "name", label: "Full name", type: "text" },
  { key: "email", label: "Email", type: "email" },
  { key: "password", label: "Password (8+ characters, a letter and a number)", type: "password" },
];

export default function Register() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", accept_terms: false });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user?.role === "client") return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data);
      toast.success("Account created. Check your inbox to verify your email.");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell eyebrow="Open an account" title="Start investing" subtitle="Create your Meridian client account in under a minute.">
      <form onSubmit={submit} className="space-y-5" data-testid="register-form">
        {FIELDS.map((f) => (
          <div key={f.key} className="space-y-2">
            <Label htmlFor={`reg-${f.key}`}>{f.label}</Label>
            <Input id={`reg-${f.key}`} type={f.type} required minLength={f.key === "password" ? 8 : undefined}
              value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="field" data-testid={`register-${f.key}-input`} />
          </div>
        ))}
        <label className="flex items-start gap-3 text-sm text-slate-400">
          <Checkbox checked={form.accept_terms} onCheckedChange={(v) => setForm({ ...form, accept_terms: !!v })} className="mt-0.5 h-5 w-5" data-testid="register-terms-checkbox" />
          <span>I agree to the <Link to="/terms" className="text-emerald-400 hover:underline">Terms</Link> and <Link to="/privacy" className="text-emerald-400 hover:underline">Privacy Policy</Link>, and I have read the <Link to="/risk-disclosure" className="text-emerald-400 hover:underline">Risk Disclosure</Link>.</span>
        </label>
        {error && <p className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400" data-testid="register-error">{error}</p>}
        <button type="submit" disabled={busy || !form.accept_terms} className="btn-primary w-full" data-testid="register-submit-button">{busy ? "Creating account..." : "Create account"}</button>
        <p className="text-center text-sm text-slate-400">
          Already have an account? <Link to="/login" className="text-emerald-400 hover:underline" data-testid="register-login-link">Log in</Link>
        </p>
      </form>
    </AuthShell>
  );
}
